import { useState, useEffect, useMemo } from 'react'
import {
  Badge,
  Box,
  Divider,
  Group,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core'
import { AreaChart, LineChart } from '@mantine/charts'
import {
  IconCpu,
  IconPlug,
  IconDatabase,
  IconArrowDown,
  IconArrowUp,
  IconHeartbeat,
  IconAlertCircle,
  IconAlertTriangle,
  IconCircleCheck,
  IconClock,
  IconBulb,
  IconSearch,
  IconChartLine,
  IconPlugConnected,
  IconDatabaseOff,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useCurrentTenant } from '@/store/auth'
import { databaseApi } from '@/api/database'
import { SmallBox, WidgetCard, CardHeader, EmptyState } from '@/components/ui'
import { EstadoDeConsulta } from '@/components/ui/EstadoDeConsulta'
// `?? 0` mostrava 0,0% de CPU em VERDE e 0,0 GB livres quando a metrica nao chegava —
// banco ocioso de disco cheio, duas afirmacoes falsas no mesmo cartao.
import { metrica } from '@/utils/metrica'

// ── Severity helpers ────────────────────────────────────────

type Severity = 'critical' | 'high' | 'medium' | 'low'

const SEVERITY_BORDER: Record<Severity, string> = {
  critical: 'var(--mantine-color-red-6)',
  high: 'var(--mantine-color-orange-6)',
  medium: 'var(--mantine-color-yellow-6)',
  low: 'var(--mantine-color-blue-6)',
}
const SEVERITY_ICON_COLOR: Record<Severity, string> = {
  critical: 'red', high: 'orange', medium: 'yellow', low: 'blue',
}
const SEVERITY_ORDER: Record<Severity, number> = { critical: 0, high: 1, medium: 2, low: 3 }

interface InsightRow {
  severity: Severity
  message: string
  icon: React.ReactNode
}

function deriveInsights(
  metrics: any,
  recommendations: any,
  slowQueries: any,
  forecast: any,
  totalStorageGb: number | undefined,
): InsightRow[] {
  const rows: InsightRow[] = []

  if (metrics) {
    const cpu = metrics.cpu_pct ?? 0
    if (cpu > 90)
      rows.push({ severity: 'critical', message: `CPU em ${cpu.toFixed(1)}% — acima do limiar crítico`, icon: <IconCpu size={14} /> })
    else if (cpu > 80)
      rows.push({ severity: 'high', message: `CPU em ${cpu.toFixed(1)}% — acima de 80%`, icon: <IconCpu size={14} /> })

    if (totalStorageGb && totalStorageGb > 0 && metrics.free_storage_gb != null) {
      const freePct = (metrics.free_storage_gb / totalStorageGb) * 100
      if (freePct < 10)
        rows.push({ severity: 'critical', message: `Storage livre em ${freePct.toFixed(1)}%`, icon: <IconDatabase size={14} /> })
      else if (freePct < 20)
        rows.push({ severity: 'high', message: `Storage livre em ${freePct.toFixed(1)}%`, icon: <IconDatabase size={14} /> })
    }

    const maxLat = Math.max(metrics.read_latency_ms ?? 0, metrics.write_latency_ms ?? 0)
    if (maxLat > 20)
      rows.push({ severity: 'high', message: `Latência de ${maxLat.toFixed(1)}ms`, icon: <IconClock size={14} /> })
    else if (maxLat > 10)
      rows.push({ severity: 'medium', message: `Latência moderada de ${maxLat.toFixed(1)}ms`, icon: <IconClock size={14} /> })
  }

  if (forecast) {
    const sd = forecast.storage?.days_until_threshold
    if (sd != null && sd < 30)
      rows.push({ severity: 'critical', message: `Storage esgota em ${sd} dias`, icon: <IconChartLine size={14} /> })
    else if (sd != null && sd < 90)
      rows.push({ severity: 'high', message: `Storage esgota em ${sd} dias`, icon: <IconChartLine size={14} /> })

    const cd = forecast.cpu?.days_until_threshold
    if (cd != null && cd < 30)
      rows.push({ severity: 'critical', message: `CPU atinge limiar em ${cd} dias`, icon: <IconChartLine size={14} /> })
    else if (cd != null && cd < 90)
      rows.push({ severity: 'high', message: `CPU atinge limiar em ${cd} dias`, icon: <IconChartLine size={14} /> })
  }

  if (recommendations?.items?.length > 0) {
    for (const rec of recommendations.items.slice(0, 3)) {
      rows.push({ severity: 'medium', message: rec.title, icon: <IconBulb size={14} /> })
    }
  }

  if (slowQueries?.count > 0)
    rows.push({ severity: 'medium', message: `${slowQueries.count} queries lentas detectadas`, icon: <IconSearch size={14} /> })

  rows.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity])
  return rows
}

// ── Helpers ─────────────────────────────────────────────────

function cpuColor(pct: number): 'danger' | 'warning' | 'success' {
  if (pct > 80) return 'danger'
  if (pct > 60) return 'warning'
  return 'success'
}

function healthColor(h: string): string {
  if (h === 'healthy') return 'green'
  if (h === 'warning') return 'yellow'
  return 'red'
}

function truncate(text: string, max: number): string {
  return text.length > max ? text.slice(0, max) + '...' : text
}

// ── Mini Chart (reusable) ───────────────────────────────────

function MiniTrendChart({ tenantId, dbId, metric, color, threshold, title }: {
  tenantId: number; dbId: string; metric: string; color: string; threshold?: number; title: string
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['metrics-history', tenantId, dbId, metric, 7],
    queryFn: () => databaseApi.getMetricsHistory(tenantId, dbId, metric, 7),
    enabled: !!tenantId && !!dbId,
    staleTime: 60_000,
  })

  const { data: forecast } = useQuery({
    queryKey: ['capacity-forecast', tenantId, dbId],
    queryFn: () => databaseApi.getCapacityForecast(tenantId, dbId),
    enabled: !!tenantId && !!dbId,
    staleTime: 120_000,
  })

  const chartData = useMemo(() => {
    if (!data?.points?.length) return []
    return data.points.map((p: any) => ({
      ts: new Date(p.ts).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
      value: Math.round(p.value * 100) / 100,
    }))
  }, [data])

  const metricKey = metric.replace('_avg_pct', '').replace('_used_pct', '')
  const fc = forecast?.[metricKey as keyof typeof forecast] as any
  const latest = chartData.length > 0 ? chartData[chartData.length - 1].value : null

  return (
    <WidgetCard>
      <Box p="sm">
        <Group justify="space-between" mb={4}>
          <Text size="xs" fw={600}>{title}</Text>
          <Group gap={6}>
            {latest != null && (
              <Text size="xs" fw={700}>{latest}%</Text>
            )}
            {fc?.growth_rate_per_day != null && fc.growth_rate_per_day > 0 && (
              <Badge size="xs" variant="light" color="orange">
                +{fc.growth_rate_per_day.toFixed(2)}/dia
              </Badge>
            )}
            {fc?.days_until_threshold != null && (
              <Badge size="xs" variant="light" color={fc.days_until_threshold < 30 ? 'red' : 'gray'}>
                {fc.days_until_threshold}d
              </Badge>
            )}
          </Group>
        </Group>
        {isLoading ? (
          <Skeleton height={120} />
        ) : chartData.length === 0 ? (
          <Text size="xs" c="dimmed" ta="center" py="md">Sem dados</Text>
        ) : (
          <AreaChart
            h={120}
            data={chartData}
            dataKey="ts"
            series={[{ name: 'value', color }]}
            curveType="monotone"
            withDots={false}
            withTooltip
            withXAxis={false}
            withYAxis={false}
            tooltipAnimationDuration={150}
            referenceLines={threshold ? [{ y: threshold, color: 'red.5', label: '' }] : undefined}
          />
        )}
      </Box>
    </WidgetCard>
  )
}

// ── DB Load Chart ───────────────────────────────────────────

function DbLoadChart({ tenantId, dbId }: { tenantId: number; dbId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['performance-insights-overview', tenantId, dbId],
    queryFn: () => databaseApi.getPerformanceInsights(tenantId, dbId, 6),
    enabled: !!tenantId && !!dbId,
    staleTime: 60_000,
  })

  const chartData = useMemo(() => {
    if (!data?.points?.length) return []
    return data.points.map((p: any) => ({
      ts: new Date(p.ts).toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      load: p.value,
    }))
  }, [data])

  return (
    <WidgetCard>
      <Box p="sm">
        <Text size="xs" fw={600} mb={4}>DB Load (AAS) — últimas 6h</Text>
        {isLoading ? (
          <Skeleton height={100} />
        ) : chartData.length === 0 ? (
          <Text size="xs" c="dimmed" ta="center" py="md">Sem dados de Performance Insights</Text>
        ) : (
          <LineChart
            h={100}
            data={chartData}
            dataKey="ts"
            series={[{ name: 'load', color: 'cyan.5' }]}
            curveType="monotone"
            withDots={false}
            withTooltip
            withXAxis={false}
            withYAxis={false}
            tooltipAnimationDuration={150}
          />
        )}
      </Box>
    </WidgetCard>
  )
}

// ── Main Component ──────────────────────────────────────────

export function OverviewTab() {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  const [selectedDb, setSelectedDb] = useState<string | null>(null)

  const instancesQuery = useQuery({
    queryKey: ['rds-instances', tenantId],
    queryFn: () => databaseApi.listInstances(tenantId!),
    enabled: !!tenantId,
    staleTime: 60_000,
  })

  useEffect(() => {
    if (instancesQuery.data?.items?.length && !selectedDb) {
      setSelectedDb(instancesQuery.data.items[0].db_identifier)
    }
  }, [instancesQuery.data, selectedDb])

  const instances = instancesQuery.data?.items ?? []
  const selectedInstance = instances.find((i: any) => i.db_identifier === selectedDb)

  // Queries
  const metricsQuery = useQuery({
    queryKey: ['rds-metrics', tenantId, selectedDb],
    queryFn: () => databaseApi.getInstanceMetrics(tenantId!, selectedDb!),
    enabled: !!tenantId && !!selectedDb,
    staleTime: 30_000,
    refetchInterval: 30_000,
  })

  const statusQuery = useQuery({
    queryKey: ['rds-status', tenantId, selectedDb],
    queryFn: () => databaseApi.getInstanceStatus(tenantId!, selectedDb!),
    enabled: !!tenantId && !!selectedDb,
    staleTime: 30_000,
  })

  const recsQuery = useQuery({
    queryKey: ['rds-recommendations-open', tenantId, selectedDb],
    queryFn: () => databaseApi.listRecommendations(tenantId!, selectedDb!, { status: 'open' }),
    enabled: !!tenantId && !!selectedDb,
    staleTime: 60_000,
  })

  const slowQuery = useQuery({
    queryKey: ['rds-slow-queries-overview', tenantId, selectedDb],
    queryFn: () => databaseApi.getSlowQueries(tenantId!, selectedDb!, 5),
    enabled: !!tenantId && !!selectedDb,
    staleTime: 60_000,
  })

  const forecastQuery = useQuery({
    queryKey: ['capacity-forecast', tenantId, selectedDb],
    queryFn: () => databaseApi.getCapacityForecast(tenantId!, selectedDb!),
    enabled: !!tenantId && !!selectedDb,
    staleTime: 120_000,
  })

  if (!tenantId) return <Text c="dimmed">Selecione um tenant</Text>

  const live = metricsQuery.data?.live
  const health = statusQuery.data?.health
  const insights = selectedDb
    ? deriveInsights(live, recsQuery.data, slowQuery.data, forecastQuery.data, selectedInstance?.storage_gb)
    : []
  const slowItems = slowQuery.data?.items ?? []

  // `insights` sai de quatro consultas. Se qualquer uma delas falhou, a lista chega curta ou
  // vazia — e a caixa afirmava "Tudo saudavel, nenhum alerta ativo". Silencio de rede virava
  // atestado de saude do banco, que e a afirmacao mais perigosa desta tela.
  const derivadasFalharam = [metricsQuery, recsQuery, slowQuery, forecastQuery].some(
    (q) => q.isError && q.data === undefined,
  )

  return (
    <Stack gap="md">
      {/* Antes: `?? []` sem ramo de erro. Com a listagem fora do ar o seletor vinha vazio, nenhum
        `selectedDb` era escolhido, e TODO o resto da aba — que depende de `selectedDb` — nao
        renderizava. A tela ficava em branco sem dizer uma palavra. */}
      <EstadoDeConsulta
        consulta={instancesQuery}
        carregando={<Skeleton width={400} height={60} />}
        vazio={instances.length === 0}
        estadoVazio={
          <EmptyState
            size="md"
            accent="primary"
            icon={IconDatabaseOff}
            title="Nenhuma instância encontrada"
            description="Configure o acesso AWS do tenant para visualizar a saúde das instâncias RDS."
          />
        }
      >
      {/* Instance selector */}
      <Group justify="space-between" align="flex-end">
        <Select
          label="Instância RDS"
          data={instances.map((i: any) => ({
            value: i.db_identifier,
            label: `${i.db_identifier} (${i.engine} · ${i.instance_class})`,
          }))}
          value={selectedDb}
          onChange={setSelectedDb}
          disabled={instancesQuery.isLoading}
          w={400}
        />
        {health && (
          <Badge
            size="lg"
            variant="light"
            color={healthColor(health)}
            leftSection={<IconHeartbeat size={14} />}
          >
            {health}
          </Badge>
        )}
      </Group>

      {selectedDb && (
        <>
          {/* KPI Strip */}
          {metricsQuery.isLoading ? (
            <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }} spacing="md">
              {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height={100} radius="md" />)}
            </SimpleGrid>
          ) : live ? (
            <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }} spacing="md">
              <SmallBox
                value={metrica(live.cpu_pct, (v) => `${v.toFixed(1)}%`)}
                label="CPU"
                icon={<IconCpu size={32} />}
                color={live.cpu_pct == null ? undefined : cpuColor(live.cpu_pct)}
              />
              <SmallBox
                value={metrica(live.connections, (v) => String(v))}
                label="Conexões"
                icon={<IconPlug size={32} />}
                color="info"
              />
              <SmallBox
                value={metrica(live.free_storage_gb, (v) => `${v.toFixed(1)} GB`)}
                label="Storage Livre"
                icon={<IconDatabase size={32} />}
                color="primary"
              />
              <SmallBox
                value={metrica(live.read_latency_ms, (v) => `${v.toFixed(2)} ms`)}
                label="Latência Leitura"
                icon={<IconArrowDown size={32} />}
                color="info"
              />
              <SmallBox
                value={metrica(live.write_latency_ms, (v) => `${v.toFixed(2)} ms`)}
                label="Latência Escrita"
                icon={<IconArrowUp size={32} />}
                color="info"
              />
            </SimpleGrid>
          ) : (
            <Text size="sm" c="dimmed" ta="center" py="md">Métricas indisponíveis</Text>
          )}

          {/* Alerts & Insights */}
          <WidgetCard>
            <Box p="sm">
              <Text size="xs" fw={600} mb={8}>Alertas & Insights</Text>
              <EstadoDeConsulta
                consulta={metricsQuery}
                falhou={derivadasFalharam}
                carregando={<Skeleton height={24} radius="sm" />}
                vazio={insights.length === 0}
                estadoVazio={
                  <Group gap="xs" py={4}>
                    <ThemeIcon size="sm" variant="light" color="green">
                      <IconCircleCheck size={14} />
                    </ThemeIcon>
                    <Text size="sm" c="dimmed">Tudo saudável — nenhum alerta ativo</Text>
                  </Group>
                }
              >
                <Stack gap={4}>
                  {insights.map((ins, idx) => (
                    <Box
                      key={idx}
                      py={6}
                      px={8}
                      style={{
                        borderLeft: `3px solid ${SEVERITY_BORDER[ins.severity]}`,
                        borderRadius: '0 var(--mantine-radius-sm) var(--mantine-radius-sm) 0',
                        background: 'var(--mantine-color-dark-6)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <ThemeIcon size="xs" variant="light" color={SEVERITY_ICON_COLOR[ins.severity]}>
                        {ins.icon}
                      </ThemeIcon>
                      <Text size="xs">{ins.message}</Text>
                    </Box>
                  ))}
                </Stack>
              </EstadoDeConsulta>
            </Box>
          </WidgetCard>

          {/* Trend Charts */}
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <MiniTrendChart
              tenantId={tenantId}
              dbId={selectedDb}
              metric="cpu_avg_pct"
              color="orange.5"
              threshold={80}
              title="CPU (7d)"
            />
            <MiniTrendChart
              tenantId={tenantId}
              dbId={selectedDb}
              metric="connections_used_pct"
              color="blue.5"
              threshold={85}
              title="Conexões (7d)"
            />
          </SimpleGrid>

          {/* DB Load */}
          <DbLoadChart tenantId={tenantId} dbId={selectedDb} />

          {/* Slow Queries */}
          {slowItems.length > 0 && (
            <WidgetCard>
              <Box p="sm">
                <Text size="xs" fw={600} mb={8}>Top Slow Queries (24h)</Text>
                <Table fz="xs" striped withColumnBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Query</Table.Th>
                      <Table.Th w={80}>Avg Load</Table.Th>
                      <Table.Th w={80}>Tempo</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {slowItems.slice(0, 5).map((q: any, i: number) => (
                      <Table.Tr key={i}>
                        <Table.Td>
                          <Tooltip label={q.query_text} multiline maw={500}>
                            <Text size="xs" ff="monospace" truncate maw={400}>
                              {truncate(q.query_text || '', 80)}
                            </Text>
                          </Tooltip>
                        </Table.Td>
                        <Table.Td>
                          <Badge size="xs" variant="light" color={q.avg_load > 0.5 ? 'red' : q.avg_load > 0.2 ? 'yellow' : 'gray'}>
                            {q.avg_load?.toFixed(3)}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed">{q.total_time_ms?.toFixed(0)}ms</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Box>
            </WidgetCard>
          )}

          {/* Open Recommendations */}
          {(recsQuery.data?.items?.length ?? 0) > 0 && (
            <WidgetCard>
              <Box p="sm">
                <Group justify="space-between" mb={8}>
                  <Text size="xs" fw={600}>Recomendações Abertas</Text>
                  <Badge size="xs" variant="light" color="orange">{recsQuery.data?.count ?? 0}</Badge>
                </Group>
                <Stack gap={4}>
                  {(recsQuery.data?.items ?? []).slice(0, 5).map((rec: any) => (
                    <Box
                      key={rec.id}
                      py={6}
                      px={8}
                      style={{
                        borderLeft: `3px solid var(--mantine-color-${rec.severity === 'critical' ? 'red' : rec.severity === 'high' ? 'orange' : 'yellow'}-6)`,
                        borderRadius: '0 var(--mantine-radius-sm) var(--mantine-radius-sm) 0',
                        background: 'var(--mantine-color-dark-6)',
                      }}
                    >
                      <Group justify="space-between">
                        <Group gap={6}>
                          <Badge size="xs" variant="light" color={rec.severity === 'critical' ? 'red' : rec.severity === 'high' ? 'orange' : 'yellow'}>
                            {rec.severity}
                          </Badge>
                          <Text size="xs">{rec.title}</Text>
                        </Group>
                        <Text size="xs" c="dimmed">{rec.type}</Text>
                      </Group>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </WidgetCard>
          )}
        </>
      )}
      </EstadoDeConsulta>
    </Stack>
  )
}
